import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Percent, Save, TrendingUp, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Populaire margin-presets voor 1-klik toepassing
const PRESETS = [
  { label: "5% (dumpprijs)", value: 5, color: "text-[#E74C3C]" },
  { label: "10% (basis)", value: 10, color: "text-[#F1C40F]" },
  { label: "12% (Black Friday)", value: 12, color: "text-[#F1C40F]" },
  { label: "15% (normaal)", value: 15, color: "text-[#27AE60]" },
  { label: "20% (premium)", value: 20, color: "text-[#27AE60]" },
  { label: "25%", value: 25, color: "text-[#3498DB]" },
];

export function PricingTab({ token }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [current, setCurrent] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchCurrent = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/pricing/margin`, { headers });
      setCurrent(data);
      setInput(String(data.margin_pct));
    } catch {
      toast.error("Kon huidige marge niet ophalen");
    }
    setLoading(false);
  };

  useEffect(() => { fetchCurrent(); }, []);

  const applyMargin = async (pct) => {
    const marginValue = 1 + Number(pct) / 100;
    if (isNaN(marginValue) || marginValue < 1.0 || marginValue > 5.0) {
      toast.error("Marge moet tussen 0% en 400% liggen");
      return;
    }
    if (!window.confirm(`Weet je zeker dat je de marge wilt veranderen naar ${pct}%? Dit past ALLE productprijzen aan.`)) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/pricing/margin`, { margin: marginValue }, { headers });
      toast.success(`${data.updated_count.toLocaleString("nl-NL")} producten aangepast naar ${data.margin_pct}% marge`);
      await fetchCurrent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Marge aanpassen mislukt");
    }
    setSaving(false);
  };

  return (
    <div data-testid="admin-pricing-tab">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <TrendingUp size={22} className="text-[#F1C40F]" /> Marge-instelling
        </h2>
        <p className="text-sm text-[#888] mt-1">
          Pas live de winstmarge aan op alle producten. Wordt onmiddellijk toegepast op de verkoopprijs = <span className="text-white font-mono">inkoop × (1 + marge)</span>.
        </p>
      </div>

      {/* Huidige status */}
      <div className="bg-gradient-to-br from-[#1a0a0a] to-[#0f0f0f] border border-[#F1C40F]/25 rounded-xl p-5 mb-6" data-testid="current-margin-panel">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] text-[#F1C40F] uppercase tracking-widest font-bold mb-1">Huidige marge</div>
            {loading ? (
              <Loader2 size={28} className="animate-spin text-[#F1C40F]" />
            ) : current ? (
              <>
                <div className="text-4xl font-bold text-white" data-testid="current-margin-value">
                  {current.margin_pct}<span className="text-2xl text-[#F1C40F]">%</span>
                </div>
                <div className="text-xs text-[#666] mt-1">
                  {current.products_with_margin.toLocaleString("nl-NL")} / {current.total_products.toLocaleString("nl-NL")} producten
                </div>
              </>
            ) : (
              <div className="text-[#666]">-</div>
            )}
          </div>
          <div className="text-xs text-[#666] max-w-sm">
            <div className="flex items-start gap-2 mb-1"><AlertCircle size={12} className="text-[#F1C40F] flex-shrink-0 mt-0.5" /><span>Verkoopprijs wordt automatisch berekend: <span className="text-[#F1C40F]">inkoopprijs × ({(1 + (current?.margin_pct || 10) / 100).toFixed(2)})</span></span></div>
            <div className="flex items-start gap-2"><AlertCircle size={12} className="text-[#F1C40F] flex-shrink-0 mt-0.5" /><span>Bestaande bestellingen blijven ongewijzigd</span></div>
          </div>
        </div>
      </div>

      {/* Presets */}
      <div className="mb-6">
        <div className="text-[10px] text-[#888] uppercase tracking-widest font-bold mb-2">Snelle keuzes</div>
        <div className="flex gap-2 flex-wrap">
          {PRESETS.map((p) => (
            <button
              key={p.value}
              onClick={() => applyMargin(p.value)}
              disabled={saving || (current && current.margin_pct === p.value)}
              data-testid={`preset-${p.value}`}
              className={`px-3 py-2 rounded-lg border transition-all text-xs font-semibold ${saving ? "opacity-40 cursor-not-allowed" : ""} ${current && current.margin_pct === p.value ? "bg-[#F1C40F]/20 border-[#F1C40F]/50 text-[#F1C40F] cursor-default" : "bg-[#0D0D0D] border-[#1E1E1E] hover:border-[#F1C40F]/40 text-white"}`}
            >
              {p.label}
              {current && current.margin_pct === p.value && <span className="ml-1.5 text-[10px]">✓ actief</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Handmatig */}
      <div className="bg-[#111] border border-[#1E1E1E] rounded-xl p-5">
        <div className="text-[10px] text-[#888] uppercase tracking-widest font-bold mb-3">Handmatig instellen</div>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 max-w-xs">
            <label className="block text-xs text-[#888] mb-1.5">Marge percentage</label>
            <div className="relative">
              <Input
                type="number"
                min="0"
                max="400"
                step="0.5"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                data-testid="margin-input"
                className="bg-[#0A0A0A] border-[#2a2a2a] text-white pr-8"
              />
              <Percent size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555]" />
            </div>
          </div>
          <div className="text-xs text-[#666]">
            = multiplier <span className="text-[#F1C40F] font-mono">{(1 + Number(input || 0) / 100).toFixed(4)}</span>
          </div>
          <Button
            onClick={() => applyMargin(input)}
            disabled={saving || !input || (current && Number(input) === current.margin_pct)}
            data-testid="apply-margin-btn"
            className="bg-[#F1C40F] text-[#111] hover:bg-[#F5D33E] font-bold"
          >
            {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
            Toepassen op alle producten
          </Button>
        </div>
        <p className="text-[10px] text-[#666] mt-3">
          Voorbeeld: retail €279 · 30% dealer-korting → inkoop €195,30 → verkoop <span className="text-white">€{(195.30 * (1 + Number(input || 0) / 100)).toFixed(2)}</span>
        </p>
      </div>
    </div>
  );
}
